import React, { useMemo, useState } from 'react';
import { Box, VStack, Button, IconButton, Icon, Image } from '@chakra-ui/react';
import {
  FiArchive,
  FiChevronLeft,
  FiChevronRight,
  FiLayers,
  FiPackage,
  FiPieChart,
  FiSettings,
  FiSlash,
  FiTool,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import { TbChartGridDots } from 'react-icons/tb';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import fullLogo from '../assets/logo_white.png';
import logoIcon from '../assets/icons/logo_icon_white.png';
import useAuth from '../context/authContext';
import { type AppRole, ROUTE_MIN_ROLE, roleHasMinimum } from '../routes/routePermissions';

type MenuItem = {
  label: string;
  icon: React.ElementType;
  to: string;
  minimumRole: AppRole;
};

const menuItems: MenuItem[] = [
  { label: 'Analytics', icon: FiPieChart, to: '/analytics', minimumRole: ROUTE_MIN_ROLE.analytics },
  { label: 'Floor Plan', icon: TbChartGridDots, to: '/floor-plan', minimumRole: ROUTE_MIN_ROLE['floor-plan'] },
  { label: 'Packages', icon: FiPackage, to: '/packages', minimumRole: ROUTE_MIN_ROLE.packages },
  { label: 'Containers', icon: FiArchive, to: '/containers', minimumRole: ROUTE_MIN_ROLE.containers },
  {
    label: 'Container Types',
    icon: FiLayers,
    to: '/container-types',
    minimumRole: ROUTE_MIN_ROLE['container-types'],
  },
  { label: 'Tasks', icon: FiTool, to: '/tasks', minimumRole: ROUTE_MIN_ROLE.tasks },
  { label: 'Disables', icon: FiSlash, to: '/disables', minimumRole: ROUTE_MIN_ROLE.disables },
  { label: 'Customers', icon: FiUser, to: '/customers', minimumRole: ROUTE_MIN_ROLE.customers },
  { label: 'Users', icon: FiUsers, to: '/users', minimumRole: ROUTE_MIN_ROLE.users },
  { label: 'Settings', icon: FiSettings, to: '/settings', minimumRole: ROUTE_MIN_ROLE.settings },
];

export default function Sidebar() {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const visibleMenuItems = useMemo(
    () => menuItems.filter(item => roleHasMinimum(user?.role, item.minimumRole)),
    [user?.role]
  );

  return (
    <Box
      as='nav'
      w={collapsed ? '24' : '56'}
      transition='width 0.2s'
      bg='sidebar.bg'
      color='sidebar.fg'
      display='flex'
      flexDirection='column'
      alignItems='center'
      py={2}
      position='relative'
    >
      {collapsed ? (
        <Box
          position='relative'
          width='60px'
          height='60px'
          mb={6}
          mt={2}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <Image src={logoIcon} alt='Company logo icon' mb={8} mt={5} boxSize='26px' objectFit='contain' />
        </Box>
      ) : (
        <Box position='relative' width='160px' height='60px' mb={8} mt={0}>
          <Image
            src={fullLogo}
            alt='Company logo wordmark'
            width='160px'
            height='60px'
            objectFit='contain'
            position='absolute'
            inset={0}
          />
        </Box>
      )}
      <VStack gap={2} align='stretch' w='full' px={collapsed ? 0 : 4}>
        {visibleMenuItems.map(item => {
          const isActive = location.pathname.startsWith(item.to);
          return (
            <RouterLink key={item.label} to={item.to} style={{ width: '100%', textDecoration: 'none' }}>
              <Button
                as='span'
                display='inline-flex'
                color='sidebar.fg'
                variant={isActive ? 'solid' : 'ghost'}
                bg={isActive ? 'sidebar.activeBg' : 'transparent'}
                _hover={{ bg: isActive ? 'sidebar.activeBg' : 'sidebar.hoverBg' }}
                justifyContent={collapsed ? 'center' : 'flex-start'}
                w='full'
                fontSize={collapsed ? 'xl' : 'md'}
                px={collapsed ? 0 : 4}
              >
                <Icon as={item.icon} />
                {!collapsed && item.label}
              </Button>
            </RouterLink>
          );
        })}
      </VStack>

      {/* <Box position='absolute' bottom={2} left={2}>
        <SiteSwitcher collapsed={collapsed} />
      </Box> */}

      <IconButton
        aria-label='Collapse sidebar'
        position='absolute'
        top={5}
        right={-4}
        zIndex={2}
        size='sm'
        onClick={() => setCollapsed(c => !c)}
        bg='sidebar.iconButtonBg'
        color='sidebar.iconButtonFg'
        border='1px solid'
        borderColor='sidebar.iconButtonBorder'
      >
        <Icon as={collapsed ? FiChevronRight : FiChevronLeft} />
      </IconButton>
    </Box>
  );
}
